import React from 'react';
import Html from 'components/html';
import type { StyledElement, AriaAttributes, TestAutomation } from 'contracts';
import math from 'utils/math';
import toArray from 'utils/to-array';

import styles from './card-carousel.module.scss';

type SwipeDirection = 'left' | 'right';

interface CardCarouselElement extends StyledElement, AriaAttributes, TestAutomation {
  selected: number;
  children: React.ReactNode;
  onSwipe?: (direction: SwipeDirection) => void;
}

interface DeckStatus {
  selected: number;
  left: number;
  right: number;
  previousSelected?: number;
}

const CardCarousel = React.forwardRef<HTMLDivElement, CardCarouselElement>((props, ref) => {
  const { selected, children, onSwipe, testId, className, style, ...rest } = props;
  const [deckStatus, setDeckStatus] = React.useState<DeckStatus>();
  const touchStartRef = React.useRef<number | null>(null);
  const touchEndRef = React.useRef<number | null>(null);

  const cardList = React.useMemo(() => {
    return React.Children.toArray(children);
  }, [children]);

  React.useEffect(() => {
    setDeckStatus((prev) => {
      const right = math.clampToBounds(selected + 1, 0, cardList.length - 1);
      const left = math.clampToBounds(selected - 1, 0, cardList.length - 1);
      const previousSelected = prev?.selected || undefined;

      return {
        selected,
        left,
        right,
        previousSelected,
      };
    });
  }, [cardList, selected]);

  const handleTouchStart = React.useCallback((ev: React.TouchEvent) => {
    touchStartRef.current = ev.touches[0].clientX;
  }, []);

  const handleTouchMove = React.useCallback((e: React.TouchEvent) => {
    touchEndRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = React.useCallback(() => {
    if (!touchStartRef.current || !touchEndRef.current) return;

    if (touchEndRef.current < touchStartRef.current) {
      onSwipe?.('right');

      return;
    }

    onSwipe?.('left');
  }, [onSwipe]);

  return (
    <Html.div
      ref={ref}
      testId={testId}
      className={[...toArray(className)]}
      style={style}
      arias={rest}
      aria-roledescription="carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Html.div className={[styles.cards, 'w-100 h-100']}>
        {cardList.map((card, index) => {
          return (
            <Html.div
              className={[
                styles.card,
                index === selected && styles.active,
                index === deckStatus?.left && styles.left,
                index === deckStatus?.right && styles.right,
                index === deckStatus?.previousSelected && styles.previousSelected,
              ]}
              // eslint-disable-next-line react/no-array-index-key
              key={`card-${index}`}
              testId={testId && `${testId}-card-${index}`}
            >
              {card}
            </Html.div>
          );
        })}
      </Html.div>
    </Html.div>
  );
});

export type { CardCarouselElement, SwipeDirection };
export default CardCarousel;
